// src/hooks/useDeepLinks.js
// Routes custom-scheme deep links (app.travy.android://…) into React Router.
//
// Why this exists: LINE Login only accepts https Callback URLs, and an https URL
// opened from the Capacitor WebView is handed to the system browser — so before
// this hook the browser finished the login while the app stayed on the login
// screen. public/line-native-callback.html now forwards LINE's response to
// app.travy.android://line-callback?code=…&state=…, the intent-filter in
// AndroidManifest.xml wakes MainActivity, and this hook turns that into a normal
// /login?code=… navigation so the existing callback handler in Login.js runs.
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

const useDeepLinks = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let listenerHandle = null;
    let cancelled = false;

    const handleUrl = (url, source) => {
      if (!url || typeof url !== 'string') return;
      console.log('[DEEPLINK] Received URL from', source, ':', url);

      // Keep the query string byte-for-byte: LINE's `code` and `state` must
      // reach Login.js exactly as issued.
      const queryIndex = url.indexOf('?');
      const search = queryIndex >= 0 ? url.slice(queryIndex) : '';

      if (url.indexOf('line-callback') !== -1) {
        // Login.js guards against replaying an already-exchanged code, so a
        // cold-start launch URL delivered twice is harmless.
        navigate(`/login${search}`, { replace: true });
        return;
      }

      if (url.indexOf('apple-callback') !== -1) {
        // Apple also names its parameter `code`, which would collide with the
        // LINE callback handler on the same /login route — so give Apple's
        // fields their own prefix before handing them to React Router.
        const incoming = new URLSearchParams(search);
        const outgoing = new URLSearchParams();
        ['code', 'id_token', 'state', 'user', 'error'].forEach((key) => {
          const value = incoming.get(key);
          if (value) outgoing.set(`apple_${key}`, value);
        });
        navigate(`/login?${outgoing.toString()}`, { replace: true });
      }
    };

    App.addListener('appUrlOpen', (event) => handleUrl(event && event.url, 'appUrlOpen'))
      .then((handle) => {
        if (cancelled) {
          handle.remove();
        } else {
          listenerHandle = handle;
        }
      })
      .catch((err) => console.warn('[DEEPLINK] Could not attach appUrlOpen listener:', err));

    // The app may have been launched cold by the deep link, in which case the
    // appUrlOpen event fires before this listener is attached.
    App.getLaunchUrl()
      .then((result) => handleUrl(result && result.url, 'getLaunchUrl'))
      .catch(() => { /* no launch URL — normal start */ });

    return () => {
      cancelled = true;
      if (listenerHandle) listenerHandle.remove();
    };
  }, [navigate]);
};

export default useDeepLinks;
